import { Button, Input } from '@xhubio-saas/core-frontend/ui-components'
import { Paperclip, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ATTACHMENT_DESCRIPTION_MAX,
  type AttachmentRejection,
  type ReviewAttachment,
  rejectAttachment,
} from './attachments'

async function toBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer())
  let raw = ''
  for (let i = 0; i < buffer.length; i += 0x8000) {
    raw += String.fromCharCode(...buffer.subarray(i, i + 0x8000))
  }
  return btoa(raw)
}

let nextId = 0

export interface AttachmentsSectionProps {
  attachments: ReviewAttachment[]
  onChange: (next: ReviewAttachment[]) => void
}

/**
 * „Anlagen (optional)" unter den Positionen (E18). Jede Datei wird vor dem Lesen gegen Typ, Anzahl und die
 * Groessen-Schwellen der API geprueft; eine abgelehnte Datei bekommt ihren Hinweis, es geht kein Aufruf hinaus.
 */
export function AttachmentsSection({ attachments, onChange }: AttachmentsSectionProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [rejections, setRejections] = useState<
    Array<{ name: string; reason: AttachmentRejection }>
  >([])

  const add = async (files: File[]) => {
    const accepted: ReviewAttachment[] = []
    const rejected: Array<{ name: string; reason: AttachmentRejection }> = []
    for (const file of files) {
      const reason = rejectAttachment(file, [...attachments, ...accepted])
      if (reason) {
        rejected.push({ name: file.name, reason })
        continue
      }
      accepted.push({
        id: `attachment-${++nextId}`,
        filename: file.name,
        size: file.size,
        content: await toBase64(file),
        description: file.name.slice(0, ATTACHMENT_DESCRIPTION_MAX),
      })
    }
    setRejections(rejected)
    if (accepted.length > 0) onChange([...attachments, ...accepted])
  }

  return (
    <section className="space-y-2" aria-labelledby="invoice-from-pdf-attachments-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id="invoice-from-pdf-attachments-title" className="text-sm font-semibold">
          {t('invoiceFromPdf.review.attachments.title')}
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          data-testid="invoice-from-pdf-attachments-add"
        >
          <Paperclip className="h-4 w-4" aria-hidden="true" />
          {t('invoiceFromPdf.review.attachments.add')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('invoiceFromPdf.review.attachments.hint')}</p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,application/pdf"
        className="hidden"
        data-testid="invoice-from-pdf-attachments-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (files.length > 0) void add(files)
        }}
      />

      {attachments.length > 0 && (
        <ul className="space-y-2" data-testid="invoice-from-pdf-attachments">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="grid grid-cols-12 items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2"
              data-testid="invoice-from-pdf-attachment"
            >
              {/* Nur der Name wird gekuerzt (voller Name im `title`); die Groesse bleibt immer sichtbar. */}
              <span className="col-span-12 flex min-w-0 items-baseline gap-1.5 text-sm sm:col-span-4">
                <span
                  className="min-w-0 truncate"
                  title={a.filename}
                  data-testid="invoice-from-pdf-attachment-name"
                >
                  {a.filename}
                </span>
                <span
                  className="shrink-0 text-muted-foreground tabular-nums"
                  data-testid="invoice-from-pdf-attachment-size"
                >
                  {t('invoiceFromPdf.review.attachments.size', {
                    kb: Math.max(1, Math.round(a.size / 1024)),
                  })}
                </span>
              </span>
              <Input
                className="col-span-10 h-9 sm:col-span-7"
                value={a.description}
                maxLength={ATTACHMENT_DESCRIPTION_MAX}
                aria-label={t('invoiceFromPdf.review.attachments.description', {
                  name: a.filename,
                })}
                onChange={(e) =>
                  onChange(
                    attachments.map((x) =>
                      x.id === a.id ? { ...x, description: e.target.value } : x
                    )
                  )
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="col-span-2 justify-self-end sm:col-span-1"
                aria-label={t('invoiceFromPdf.review.attachments.remove', { name: a.filename })}
                onClick={() => onChange(attachments.filter((x) => x.id !== a.id))}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {rejections.length > 0 && (
        <ul className="space-y-1" role="alert" data-testid="invoice-from-pdf-attachments-rejected">
          {rejections.map((r) => (
            <li
              key={`${r.name}-${r.reason}`}
              className="text-sm text-destructive"
              data-reason={r.reason}
            >
              {t(`invoiceFromPdf.review.attachments.rejected.${r.reason}`, { name: r.name })}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
